/**
 * InfiniteChainPanel - Panel for "Chaînage Infini (Start/End)" mode
 *
 * Displays in the right panel, similar to PromptConception.
 * Allows building a sequence of Start → End image pairs with prompts between them.
 *
 * Features:
 * - Start image editable via Nano Pro (like other modes)
 * - End image auto-generated or extracted from video
 * - Action prompt between Start and End
 * - Auto-calculation of segments based on total duration (>8s = multiple segments)
 * - Batch video generation for all segments
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  SparklesIcon,
  ArrowRightIcon,
  PlusIcon,
  TrashIcon,
  PlayIcon,
  FilmIcon,
  UploadCloudIcon,
  CheckIcon,
  ScissorsIcon,
} from './icons';
import { ImageFile, Dogma, Character } from '../types';

export interface ChainSegment {
  id: string;
  /** Index in the chain (0, 1, 2, ...) */
  index: number;
  /** Start image (first frame) */
  startImage: ImageFile | null;
  /** End image (last frame) */
  endImage: ImageFile | null;
  /** Action prompt describing what happens between start and end */
  actionPrompt: string;
  /** End image prompt (optional - for generating end image) */
  endImagePrompt: string;
  /** Status of segment */
  status: 'draft' | 'ready' | 'generating_image' | 'generating_video' | 'complete' | 'error';
  /** Generated video blob (if any) */
  videoBlob?: Blob;
  /** Error message if failed */
  error?: string;
}

interface InfiniteChainPanelProps {
  /** Total target duration in seconds */
  totalDuration: number;
  /** Maximum duration per segment (Veo 3.1 = 8s) */
  maxSegmentDuration?: number;
  /** Active dogma for generation */
  activeDogma: Dogma | null;
  /** Available characters */
  characters: Character[];
  /** API key for generation */
  apiKey: string | null;
  /** Chain segments state */
  segments: ChainSegment[];
  /** Update segments callback */
  onSegmentsChange: (segments: ChainSegment[]) => void;
  /** Callback to open Nano editor for an image */
  onOpenNanoEditor?: (
    segmentId: string,
    targetImage: 'start' | 'end',
    baseImage: ImageFile | null,
    initialPrompt: string
  ) => void;
  /** Callback to open video frame extractor */
  onOpenFrameExtractor?: (
    segmentId: string,
    targetImage: 'start' | 'end'
  ) => void;
  /** Callback to generate end image */
  onGenerateEndImage?: (segmentId: string, prompt: string) => Promise<void>;
  /** Callback to generate video for a segment */
  onGenerateVideo?: (segmentId: string) => Promise<void>;
  /** Callback to batch generate all videos */
  onBatchGenerateVideos?: () => Promise<void>;
  /** Is batch generation in progress */
  isBatchGenerating?: boolean;
}

export const InfiniteChainPanel: React.FC<InfiniteChainPanelProps> = ({
  totalDuration,
  maxSegmentDuration = 8,
  activeDogma,
  characters,
  apiKey,
  segments,
  onSegmentsChange,
  onOpenNanoEditor,
  onOpenFrameExtractor,
  onGenerateEndImage,
  onGenerateVideo,
  onBatchGenerateVideos,
  isBatchGenerating = false,
}) => {
  // Calculate recommended number of segments
  const recommendedSegments = Math.ceil(totalDuration / maxSegmentDuration);
  const currentSegmentCount = segments.length;
  const needsMoreSegments = currentSegmentCount < recommendedSegments;

  // Add new segment
  const addSegment = useCallback(() => {
    const lastSegment = segments[segments.length - 1];
    const newSegment: ChainSegment = {
      id: `chain-${Date.now()}`,
      index: segments.length,
      // If there's a previous segment with an end image, use it as start
      startImage: lastSegment?.endImage || null,
      endImage: null,
      actionPrompt: '',
      endImagePrompt: '',
      status: 'draft',
    };
    onSegmentsChange([...segments, newSegment]);
  }, [segments, onSegmentsChange]);

  // Remove segment
  const removeSegment = useCallback((segmentId: string) => {
    const updated = segments.filter(s => s.id !== segmentId);
    // Reindex remaining segments
    const reindexed = updated.map((s, idx) => ({ ...s, index: idx }));
    onSegmentsChange(reindexed);
  }, [segments, onSegmentsChange]);

  // Update segment
  const updateSegment = useCallback((segmentId: string, update: Partial<ChainSegment>) => {
    const updated = segments.map(s =>
      s.id === segmentId ? { ...s, ...update } : s
    );
    onSegmentsChange(updated);
  }, [segments, onSegmentsChange]);

  // Check if all segments are ready for batch generation
  const allSegmentsReady = segments.length > 0 && segments.every(s =>
    s.startImage && s.endImage && s.actionPrompt.trim() && s.status !== 'generating_video'
  );

  // Count segments ready for video generation
  const readyForVideoCount = segments.filter(s =>
    s.startImage && s.endImage && s.actionPrompt.trim() && s.status !== 'complete'
  ).length;

  // Initialize with one segment if empty
  useEffect(() => {
    if (segments.length === 0) {
      addSegment();
    }
  }, []);

  return (
    <div className="bg-[#1f1f1f] border border-gray-700 rounded-2xl h-full flex flex-col shadow-lg p-4 overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 mb-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <span className="text-xl">🔗</span>
          Chaînage Infini
        </h3>
        <p className="text-xs text-gray-400 mt-1">
          Créez une séquence d'images. Chaque fin devient le départ suivant.
        </p>
      </div>

      {/* Duration Info */}
      <div className="flex-shrink-0 bg-gradient-to-r from-blue-900/30 to-cyan-900/30 p-3 rounded-lg border border-blue-500/20 mb-4">
        <div className="flex items-center justify-between text-xs">
          <span className="text-blue-300">
            Durée totale: <strong>{totalDuration}s</strong>
          </span>
          <span className="text-cyan-300">
            Segments: <strong>{currentSegmentCount}</strong> / {recommendedSegments} recommandés
          </span>
        </div>
        {needsMoreSegments && (
          <p className="text-[10px] text-yellow-400/80 mt-1">
            Pour {totalDuration}s, créez {recommendedSegments} segments de ~{maxSegmentDuration}s max.
          </p>
        )}
      </div>

      {/* Segments List - Scrollable */}
      <div className="flex-grow overflow-y-auto space-y-4 pr-1">
        {segments.map((segment, idx) => (
          <SegmentCard
            key={segment.id}
            segment={segment}
            isFirst={idx === 0}
            isLast={idx === segments.length - 1}
            onUpdate={(update) => updateSegment(segment.id, update)}
            onRemove={() => removeSegment(segment.id)}
            onOpenNanoEditor={onOpenNanoEditor}
            onOpenFrameExtractor={onOpenFrameExtractor}
            onGenerateEndImage={onGenerateEndImage}
            onGenerateVideo={onGenerateVideo}
            activeDogma={activeDogma}
          />
        ))}

        {/* Add Segment Button */}
        <button
          onClick={addSegment}
          className="w-full py-3 border-2 border-dashed border-gray-700 hover:border-indigo-500 rounded-xl text-gray-500 hover:text-indigo-400 text-sm font-medium flex items-center justify-center gap-2 transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
          Ajouter un segment
        </button>
      </div>

      {/* Batch Generate Button */}
      <div className="flex-shrink-0 mt-4 pt-4 border-t border-gray-700">
        <button
          onClick={onBatchGenerateVideos}
          disabled={!allSegmentsReady || isBatchGenerating}
          className={`w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all ${
            allSegmentsReady && !isBatchGenerating
              ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white shadow-lg shadow-green-900/30'
              : 'bg-gray-700 text-gray-500 cursor-not-allowed'
          }`}
        >
          {isBatchGenerating ? (
            <>
              <div className="w-4 h-4 border-2 border-t-transparent border-white rounded-full animate-spin" />
              Génération en cours...
            </>
          ) : (
            <>
              <FilmIcon className="w-4 h-4" />
              Générer {readyForVideoCount > 0 ? `${readyForVideoCount} vidéo${readyForVideoCount > 1 ? 's' : ''}` : 'toutes les vidéos'}
            </>
          )}
        </button>
        {!allSegmentsReady && segments.length > 0 && (
          <p className="text-[10px] text-gray-500 text-center mt-2">
            Complétez tous les segments (Start, End, Prompt) pour activer la génération batch.
          </p>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Segment Card Component
// ============================================================================

interface SegmentCardProps {
  segment: ChainSegment;
  isFirst: boolean;
  isLast: boolean;
  onUpdate: (update: Partial<ChainSegment>) => void;
  onRemove: () => void;
  onOpenNanoEditor?: InfiniteChainPanelProps['onOpenNanoEditor'];
  onOpenFrameExtractor?: InfiniteChainPanelProps['onOpenFrameExtractor'];
  onGenerateEndImage?: InfiniteChainPanelProps['onGenerateEndImage'];
  onGenerateVideo?: InfiniteChainPanelProps['onGenerateVideo'];
  activeDogma: Dogma | null;
}

const SegmentCard: React.FC<SegmentCardProps> = ({
  segment,
  isFirst,
  isLast,
  onUpdate,
  onRemove,
  onOpenNanoEditor,
  onOpenFrameExtractor,
  onGenerateEndImage,
  onGenerateVideo,
  activeDogma,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isGeneratingEnd, setIsGeneratingEnd] = useState(false);

  const handleGenerateEnd = async () => {
    if (!onGenerateEndImage || !segment.endImagePrompt.trim()) return;
    setIsGeneratingEnd(true);
    try {
      await onGenerateEndImage(segment.id, segment.endImagePrompt);
    } finally {
      setIsGeneratingEnd(false);
    }
  };

  const isReady = segment.startImage && segment.endImage && segment.actionPrompt.trim();
  const statusColors = {
    draft: 'bg-gray-600',
    ready: 'bg-blue-600',
    generating_image: 'bg-yellow-600',
    generating_video: 'bg-purple-600',
    complete: 'bg-green-600',
    error: 'bg-red-600',
  };

  return (
    <div className={`bg-gray-900/50 rounded-xl border ${
      segment.status === 'complete' ? 'border-green-500/30' :
      segment.status === 'error' ? 'border-red-500/30' :
      'border-gray-700'
    } overflow-hidden transition-all`}>
      {/* Segment Header */}
      <div
        className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-800/30 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <div className={`w-6 h-6 rounded-full ${statusColors[segment.status]} flex items-center justify-center text-xs font-bold text-white`}>
            {segment.index + 1}
          </div>
          <span className="text-sm font-medium text-white">
            Segment {segment.index + 1}
          </span>
          {isReady && segment.status !== 'complete' && (
            <span className="px-2 py-0.5 bg-blue-900/50 text-blue-300 text-[10px] rounded-full">
              Prêt
            </span>
          )}
          {segment.status === 'complete' && (
            <span className="px-2 py-0.5 bg-green-900/50 text-green-300 text-[10px] rounded-full flex items-center gap-1">
              <CheckIcon className="w-3 h-3" /> Complet
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!isFirst && (
            <button
              onClick={(e) => { e.stopPropagation(); onRemove(); }}
              className="p-1 text-gray-500 hover:text-red-400 transition-colors"
              title="Supprimer"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          )}
          <svg
            className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="p-3 pt-0 space-y-3">
          {/* Images Row: Start → End */}
          <div className="flex items-stretch gap-3">
            {/* Start Image */}
            <div className="flex-1">
              <div className="text-[10px] uppercase font-bold text-gray-500 mb-1 tracking-wider">
                Image Départ
              </div>
              <ImageSlot
                image={segment.startImage}
                placeholder="Départ"
                onOpenNano={() => onOpenNanoEditor?.(segment.id, 'start', segment.startImage, segment.actionPrompt)}
                onOpenExtractor={() => onOpenFrameExtractor?.(segment.id, 'start')}
                onUpload={(img) => onUpdate({ startImage: img })}
                disabled={segment.status === 'generating_video' || segment.status === 'complete'}
              />
            </div>

            {/* Arrow */}
            <div className="flex items-center justify-center px-2">
              <div className="flex flex-col items-center gap-1">
                <ArrowRightIcon className="w-5 h-5 text-indigo-400" />
                <span className="text-[8px] text-gray-600">action</span>
              </div>
            </div>

            {/* End Image */}
            <div className="flex-1">
              <div className="text-[10px] uppercase font-bold text-gray-500 mb-1 tracking-wider">
                Image Fin
              </div>
              <ImageSlot
                image={segment.endImage}
                placeholder="Fin"
                onOpenNano={() => onOpenNanoEditor?.(segment.id, 'end', segment.endImage, segment.endImagePrompt)}
                onOpenExtractor={() => onOpenFrameExtractor?.(segment.id, 'end')}
                onUpload={(img) => onUpdate({ endImage: img })}
                disabled={segment.status === 'generating_video' || segment.status === 'complete'}
              />
            </div>
          </div>

          {/* Action Prompt */}
          <div>
            <div className="text-[10px] uppercase font-bold text-gray-500 mb-1 tracking-wider">
              Action (ce qui se passe)
            </div>
            <textarea
              value={segment.actionPrompt}
              onChange={(e) => onUpdate({ actionPrompt: e.target.value })}
              placeholder="Décrivez l'action entre les deux images..."
              className="w-full bg-gray-800 border border-gray-700 rounded-lg p-2 text-xs text-gray-200 resize-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 h-16"
              disabled={segment.status === 'generating_video' || segment.status === 'complete'}
            />
          </div>

          {/* End Image Generation (if no end image yet) */}
          {!segment.endImage && (
            <div className="bg-gray-800/50 p-2 rounded-lg border border-gray-700">
              <div className="text-[10px] uppercase font-bold text-cyan-500 mb-1 tracking-wider">
                Générer l'image de fin
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={segment.endImagePrompt}
                  onChange={(e) => onUpdate({ endImagePrompt: e.target.value })}
                  placeholder="Décrivez l'image de fin..."
                  className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-2 py-1.5 text-xs text-gray-200 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                />
                <button
                  onClick={handleGenerateEnd}
                  disabled={!segment.endImagePrompt.trim() || isGeneratingEnd}
                  className="px-3 py-1.5 bg-cyan-600 hover:bg-cyan-500 disabled:bg-gray-700 text-white text-xs font-bold rounded-lg flex items-center gap-1"
                >
                  {isGeneratingEnd ? (
                    <div className="w-3 h-3 border-2 border-t-transparent border-white rounded-full animate-spin" />
                  ) : (
                    <SparklesIcon className="w-3 h-3" />
                  )}
                  Générer
                </button>
              </div>
            </div>
          )}

          {/* Generate Video Button (per segment) */}
          {isReady && segment.status !== 'complete' && (
            <button
              onClick={() => onGenerateVideo?.(segment.id)}
              disabled={segment.status === 'generating_video'}
              className="w-full py-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-gray-700 text-white text-xs font-bold rounded-lg flex items-center justify-center gap-2"
            >
              {segment.status === 'generating_video' ? (
                <>
                  <div className="w-3 h-3 border-2 border-t-transparent border-white rounded-full animate-spin" />
                  Génération vidéo...
                </>
              ) : (
                <>
                  <PlayIcon className="w-3 h-3" />
                  Générer la vidéo
                </>
              )}
            </button>
          )}

          {/* Error Display */}
          {segment.error && (
            <div className="p-2 bg-red-900/30 border border-red-500/30 rounded-lg text-xs text-red-300">
              {segment.error}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Image Slot Component
// ============================================================================

interface ImageSlotProps {
  image: ImageFile | null;
  placeholder: string;
  onOpenNano?: () => void;
  onOpenExtractor?: () => void;
  onUpload: (image: ImageFile) => void;
  disabled?: boolean;
}

const ImageSlot: React.FC<ImageSlotProps> = ({
  image,
  placeholder,
  onOpenNano,
  onOpenExtractor,
  onUpload,
  disabled = false,
}) => {
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = (reader.result as string).split(',')[1];
      onUpload({ file, base64 });
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className={`aspect-video bg-gray-900 rounded-lg border border-gray-700 relative group overflow-hidden ${
      disabled ? 'opacity-60' : ''
    }`}>
      {image ? (
        <>
          <img
            src={image.file ? URL.createObjectURL(image.file) : `data:image/jpeg;base64,${image.base64}`}
            className="w-full h-full object-cover"
            alt={placeholder}
          />
          {!disabled && (
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              {onOpenNano && (
                <button
                  onClick={onOpenNano}
                  className="p-1.5 bg-orange-600 rounded-full text-white hover:bg-orange-500"
                  title="Éditer avec Nano"
                >
                  <SparklesIcon className="w-3 h-3" />
                </button>
              )}
              {onOpenExtractor && (
                <button
                  onClick={onOpenExtractor}
                  className="p-1.5 bg-purple-600 rounded-full text-white hover:bg-purple-500"
                  title="Extraire d'une vidéo"
                >
                  <ScissorsIcon className="w-3 h-3" />
                </button>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center text-gray-600">
          {!disabled ? (
            <>
              <div className="flex gap-2 mb-2">
                {onOpenNano && (
                  <button
                    onClick={onOpenNano}
                    className="p-2 bg-gray-800 hover:bg-orange-600/20 border border-gray-700 hover:border-orange-500/50 rounded-lg transition-colors"
                    title="Créer avec Nano"
                  >
                    <SparklesIcon className="w-4 h-4" />
                  </button>
                )}
                {onOpenExtractor && (
                  <button
                    onClick={onOpenExtractor}
                    className="p-2 bg-gray-800 hover:bg-purple-600/20 border border-gray-700 hover:border-purple-500/50 rounded-lg transition-colors"
                    title="Extraire d'une vidéo"
                  >
                    <ScissorsIcon className="w-4 h-4" />
                  </button>
                )}
                <label className="p-2 bg-gray-800 hover:bg-indigo-600/20 border border-gray-700 hover:border-indigo-500/50 rounded-lg cursor-pointer transition-colors">
                  <UploadCloudIcon className="w-4 h-4" />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </label>
              </div>
              <span className="text-[9px]">{placeholder}</span>
            </>
          ) : (
            <span className="text-[9px]">En attente...</span>
          )}
        </div>
      )}
    </div>
  );
};

export default InfiniteChainPanel;
